import { Link } from 'react-router-dom';
import { SafeSwipeDemo } from '../components/SafeSwipeDemo';
import { ResumeBuilderDemo } from '../components/ResumeBuilderDemo';
import {
  MessageCircle,
  Shield,
  Zap,
  Target,
  CheckCircle,
  Heart,
  Sparkles,
  Coins,
  ArrowRight,
} from 'lucide-react';

export function Landing() {
  return (
    <div className="min-h-screen bg-white">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-8 h-8 text-cyan-500" />
              <span className="text-xl font-bold text-gray-900">ResuSwipe</span>
            </div>
            <div className="flex items-center gap-3">
              <Link
                to="/signup"
                className="px-6 py-2.5 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-lg font-semibold hover:from-orange-600 hover:to-amber-600 transition-all shadow-sm hover:shadow-md"
              >
                Join Waitlist
              </Link>
              <Link
                to="/login"
                className="px-6 py-2.5 text-gray-700 font-semibold hover:text-cyan-600 transition-colors"
              >
                Sign In
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <section className="relative pt-32 pb-32 px-4 bg-gradient-to-b from-[#56F3E8] via-[#A7E8F0] to-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_var(--tw-gradient-stops))] from-cyan-400/10 via-transparent to-transparent" />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <h1 className="text-6xl sm:text-7xl md:text-8xl font-bold text-gray-900 mb-4 leading-[1.1]">
              Your future,<br />one text away.
            </h1>

            <h2 className="text-3xl sm:text-4xl md:text-5xl font-semibold text-gray-800 mb-6 leading-tight">
              Swipe. Chat. Get hired.
            </h2>

            <p className="text-xl sm:text-2xl text-gray-700 mb-10 max-w-4xl mx-auto leading-relaxed font-medium">
              Designed for people. Enhanced by AI. Creating the future you can swipe into.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
              <Link
                to="/signup"
                className="px-10 py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-bold hover:from-orange-600 hover:to-amber-600 transition-all shadow-lg hover:shadow-xl text-lg"
              >
                Get Started Free
              </Link>
              <Link
                to="/login"
                className="px-10 py-4 bg-white text-gray-800 rounded-xl font-bold hover:bg-gray-50 transition-all shadow-md hover:shadow-lg text-lg border-2 border-gray-200"
              >
                Sign In
              </Link>
            </div>
          </div>

          <div className="mt-32">
            <div className="text-center mb-20 max-w-6xl mx-auto">
              <p className="text-lg sm:text-xl text-gray-800 leading-relaxed mb-4">
                Resume polish, tracking, and reminders are taken care of, helping you swipe right on your future.
              </p>
              <p className="text-base text-gray-600">
                See our platform in action with two powerful features
              </p>
            </div>

            <div className="space-y-20 max-w-[1000px] mx-auto">
              <div className="space-y-12">
                <h3 className="text-3xl font-bold text-gray-900 text-center">Swipe to Apply</h3>
                <div className="flex items-center justify-center">
                  <SafeSwipeDemo />
                </div>

                <h3 className="text-3xl font-bold text-gray-900 text-center mt-16">Build Your Resume</h3>
                <div className="w-full">
                  <ResumeBuilderDemo />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Built for humans. Powered by AI.
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to accelerate your career
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition">
              <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center mb-6">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Swipe to Apply</h3>
              <p className="text-gray-600 leading-relaxed">
                Discover jobs like you're browsing your favorite app. Swipe right to apply, left to skip — it's simple, quick, and intuitive.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition">
              <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center mb-6">
                <MessageCircle className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Text-First Messaging</h3>
              <p className="text-gray-600 leading-relaxed">
                Chat directly with your personal AI career assistant or employers. Ask questions, refine your resume, and get real-time feedback.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition">
              <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center mb-6">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Privacy-First</h3>
              <p className="text-gray-600 leading-relaxed">
                Your data stays yours. ResuSwipe uses end-to-end encrypted, ZeroDB-style secure storage — meaning even we can't see your resume, messages, or activity.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition">
              <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center mb-6">
                <Target className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">Smarter Job Matching</h3>
              <p className="text-gray-600 leading-relaxed">
                Better matches. Better outcomes. ResuSwipe analyzes your skills, goals, personality, and experience to recommend the roles that truly fit you.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Simple. Smart. Secure.
            </h2>
            <p className="text-xl text-gray-600">
              Get started in three easy steps
            </p>
          </div>

          <div className="space-y-12">
            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-16 h-16 bg-cyan-500 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-white">1</span>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Tell us what you're looking for
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Text our AI Chief of Staff about your dream role, skills, and goals. It builds your profile instantly — no forms required.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-white">2</span>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Swipe through curated opportunities
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Our AI matches you with perfect-fit jobs. Swipe right to apply, left to pass. It's that simple.
                </p>
              </div>
            </div>

            <div className="flex gap-6 items-start">
              <div className="flex-shrink-0 w-16 h-16 bg-cyan-500 rounded-full flex items-center justify-center">
                <span className="text-2xl font-bold text-white">3</span>
              </div>
              <div className="flex-1">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Chat, interview, and get hired
                </h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  Message employers directly, schedule interviews via Zoom, and earn rewards for every step forward.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-gradient-to-br from-cyan-500 to-blue-600 text-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-8">
              <Coins className="w-8 h-8 text-white" />
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6">
              How Credits Power Your Job Search
            </h2>

            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              ResuSwipe uses a simple credits system to unlock premium features and accelerate your career journey.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                  <CheckCircle className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold">Earn Credits</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-green-300 font-bold text-lg flex-shrink-0">+100</span>
                  <span className="text-white/90">Signup bonus welcome gift</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-300 font-bold text-lg flex-shrink-0">+5</span>
                  <span className="text-white/90">Every job you swipe right on</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-300 font-bold text-lg flex-shrink-0">+10</span>
                  <span className="text-white/90">Completing your profile</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-300 font-bold text-lg flex-shrink-0">+15</span>
                  <span className="text-white/90">Uploading your first resume</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-300 font-bold text-lg flex-shrink-0">+25</span>
                  <span className="text-white/90">When friends join with your code</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-green-300 font-bold text-lg flex-shrink-0">+75</span>
                  <span className="text-white/90">When they upgrade to premium</span>
                </li>
              </ul>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-2xl font-bold">Spend Credits</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <span className="text-orange-300 font-bold text-lg flex-shrink-0">-10</span>
                  <span className="text-white/90">Build a new AI-powered resume</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-orange-300 font-bold text-lg flex-shrink-0">-8</span>
                  <span className="text-white/90">Completely refresh your resume</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-orange-300 font-bold text-lg flex-shrink-0">-5</span>
                  <span className="text-white/90">Generate custom cover letter</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-orange-300 font-bold text-lg flex-shrink-0">-2-5</span>
                  <span className="text-white/90">AI-powered job applications</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-orange-300 font-bold text-lg flex-shrink-0">-4</span>
                  <span className="text-white/90">Add new job to your resume</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-orange-300 font-bold text-lg flex-shrink-0">-2</span>
                  <span className="text-white/90">Improve resume bullet points</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center">
            <p className="text-xl mb-6 text-white/90">
              Start with 100 free credits. Earn more as you engage. Buy credit packs anytime to unlock premium features faster.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-cyan-600 rounded-xl font-bold hover:bg-gray-100 transition shadow-lg"
              >
                Get 100 Free Credits
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white/20 backdrop-blur text-white rounded-xl font-bold hover:bg-white/30 transition border-2 border-white/30"
              >
                View Credit Packs
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-gray-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-cyan-500/20 rounded-full mb-8">
            <Shield className="w-8 h-8 text-cyan-400" />
          </div>

          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            Your data. Your keys. 100% encrypted.
          </h2>

          <p className="text-xl mb-8 text-gray-300">
            You deserve a job platform that protects your information.
          </p>

          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 mb-8">
            <p className="text-lg mb-6 text-gray-300">
              ResuSwipe uses ZeroDB-style, client-side encryption, ensuring:
            </p>

            <ul className="text-left space-y-4 max-w-2xl mx-auto">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5 text-cyan-400" />
                <span className="text-lg">No one sees your data but you</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5 text-cyan-400" />
                <span className="text-lg">Every message and resume is encrypted before it leaves your device</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5 text-cyan-400" />
                <span className="text-lg">Even our servers store only secured, unreadable records</span>
              </li>
            </ul>
          </div>

          <p className="text-2xl font-medium text-cyan-400">
            Privacy isn't a feature — it's the foundation.
          </p>
        </div>
      </section>

      <section className="py-20 px-4 bg-gradient-to-br from-orange-500 to-amber-500 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            Built for the people who power our cities.
          </h2>

          <p className="text-xl mb-8 text-white/90 leading-relaxed">
            ResuSwipe was created in Detroit with a mission to bring opportunity to every community — students, parents, career-changers, job seekers in tough transitions, and anyone rebuilding their future.
          </p>

          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 mb-8">
            <p className="text-lg mb-6 font-semibold">We're committed to:</p>

            <ul className="text-left space-y-3 max-w-2xl mx-auto text-lg">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
                <span>Fair, unbiased AI</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
                <span>Community access and equity</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
                <span>Human-centered support</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />
                <span>Job matching that includes — not excludes</span>
              </li>
            </ul>
          </div>

          <p className="text-2xl font-medium">
            Detroit is where it starts.
            <br />
            Your journey is where it grows.
          </p>
        </div>
      </section>

      <section className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-2xl p-8">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">For Job Seekers</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5 text-cyan-600" />
                  <span className="text-lg text-gray-700">Swipe-to-Apply</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5 text-cyan-600" />
                  <span className="text-lg text-gray-700">AI resume builder</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5 text-cyan-600" />
                  <span className="text-lg text-gray-700">Private, encrypted messaging</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5 text-cyan-600" />
                  <span className="text-lg text-gray-700">Personalized job suggestions</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5 text-cyan-600" />
                  <span className="text-lg text-gray-700">Interview scheduling</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5 text-cyan-600" />
                  <span className="text-lg text-gray-700">Rewards + progress tracking</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5 text-cyan-600" />
                  <span className="text-lg text-gray-700">Supportive coaching through text</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-8">
              <h3 className="text-3xl font-bold text-gray-900 mb-6">For Employers</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5 text-orange-600" />
                  <span className="text-lg text-gray-700">Pre-qualified candidates</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5 text-orange-600" />
                  <span className="text-lg text-gray-700">AI-generated applicant summaries</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5 text-orange-600" />
                  <span className="text-lg text-gray-700">One-click Zoom scheduling</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5 text-orange-600" />
                  <span className="text-lg text-gray-700">Slack + Outlook integration</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5 text-orange-600" />
                  <span className="text-lg text-gray-700">Easy posting & fast communication</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5 text-orange-600" />
                  <span className="text-lg text-gray-700">Pay-per-hire or subscription options</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5 text-orange-600" />
                  <span className="text-lg text-gray-700">Trusted, privacy-first talent network</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-3xl p-12 text-center text-white shadow-2xl">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-6">
              <Heart className="w-8 h-8 text-white" />
            </div>

            <h2 className="text-4xl sm:text-5xl font-bold mb-6">
              Ready to transform your career?
            </h2>

            <p className="text-xl mb-8 text-white/90 leading-relaxed">
              Join thousands already finding their dream jobs through ResuSwipe's AI-powered platform.
            </p>

            <Link
              to="/signup"
              className="inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-xl font-semibold hover:from-orange-600 hover:to-amber-600 transition shadow-lg text-lg"
            >
              Get Started Free →
            </Link>

            <p className="mt-8 text-white/80">
              Built for humans. Powered by AI. Protected by privacy.
            </p>
          </div>
        </div>
      </section>

      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <MessageCircle className="w-8 h-8 text-cyan-400" />
              <span className="text-xl font-bold">ResuSwipe</span>
            </div>

            <div className="text-center md:text-right">
              <p className="text-gray-400 mb-2">
                © 2025 ResuSwipe — Built for humans. Powered by AI.
              </p>
              <div className="flex items-center gap-6 justify-center md:justify-end text-sm">
                <a href="#" className="text-gray-400 hover:text-white transition">
                  Privacy Policy
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition">
                  Terms of Use
                </a>
                <a href="#" className="text-gray-400 hover:text-white transition">
                  Contact
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
